import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SellerContactsRow } from '~/components/common/SellerContactsRow';
import { SellerOrganizationPicker } from '~/components/common/SellerOrganizationPicker';
import { toggleId } from '~/utils/toggleId';
import { useBuyListSellerPickerLabels } from '~/components/common/useBuyListSellerPickerLabels';
import { SellerListToggles } from './SellerListToggles';
import type { SellerRecipients } from './useSellerRecipients';

type SelectSellerContactsStepProps = {
  recipients: SellerRecipients;
  isSubmitting: boolean;
};

export function SelectSellerContactsStep({
  recipients,
  isSubmitting,
}: Readonly<SelectSellerContactsStepProps>) {
  const { t } = useTranslation('inventory');
  const [expandedOrganizationIds, setExpandedOrganizationIds] = useState<
    Set<string>
  >(new Set());
  const sellerPickerLabels = useBuyListSellerPickerLabels({
    loading: t('inventory.buy_list.request_for_quote.sellers_loading'),
    error: t('inventory.buy_list.request_for_quote.sellers_load_failed'),
    emptyMessage: t('inventory.buy_list.request_for_quote.no_sellers'),
  });
  const rowLabels = {
    contactCount: (count: number) =>
      t('inventory.buy_list.request_for_quote.contact_count', { count }),
    noContacts: t('inventory.buy_list.request_for_quote.no_contacts'),
    selectedCount: (count: number) =>
      t('inventory.buy_list.request_for_quote.selected_count', { count }),
    selectOrganization: (name: string) =>
      t('inventory.buy_list.request_for_quote.select_organization', { name }),
  };

  const selectedOrganizationIds = useMemo(
    () => [...recipients.selectedOrganizationIds],
    [recipients.selectedOrganizationIds]
  );

  const toggleExpand = (organizationId: string) => {
    setExpandedOrganizationIds((previous) =>
      toggleId(previous, organizationId)
    );
  };

  return (
    <div className="flex flex-col gap-3">
      <SellerListToggles recipients={recipients} />
      <SellerOrganizationPicker
        testId="rfq-sellers"
        pinnedOrganizationIds={selectedOrganizationIds}
        labels={sellerPickerLabels}
        renderOrganization={(organization, listContact) => (
          <SellerContactsRow
            organization={organization}
            recipients={recipients}
            labels={rowLabels}
            testId={`rfq-seller-row-${organization.id}`}
            checkboxIdPrefix={`select-contact-${organization.id}`}
            expanded={expandedOrganizationIds.has(organization.id)}
            onToggleExpand={() => toggleExpand(organization.id)}
            onContactAdded={listContact}
            addContactDisabled={isSubmitting}
          />
        )}
      />
    </div>
  );
}
